import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield, Handshake, FileCheck, Building2, Users, HardHat, Home, CheckCircle, Star, Award, TrendingUp, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function GarantiesSolidairesBTPPage() {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const ecosysteme = [
    {
      icon: "🤝",
      title: "COPPAM – Garanties Solidaires",
      description: "Partenaire distinct de la SCIC : accueil, pré-diagnostic, accompagnement, médiation et montage des dossiers dans le cadre de la coopération.",
      color: "from-amber-500 to-yellow-500"
    },
    {
      icon: "📋",
      title: "F.O.G.E.D.O.M – Instruction",
      description: "Instruction sociale, territoriale et technique. Diagnostic, vérification et rapport d'instruction transmis au Fonds FOGEDOM-SCIC.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: "🔎",
      title: "FOGEDOM-SCIC – Fonds interne",
      description: "Section analytique d’OBJECTIF SCOP OUTREMER, sans personnalité juridique distincte. Soutien, prévention et ingénierie selon les statuts et le règlement du fonds.",
      color: "from-emerald-500 to-green-500"
    },
    {
      icon: "🏛️",
      title: "SCIC O'SCOP – Gouvernance",
      description: "Gestion administrative, coordination, facturation, encaissement et supervision du dispositif coopératif multi-acteurs.",
      color: "from-purple-500 to-indigo-500"
    }
  ];

  const garanties = [
    {
      title: "BATICO — RCDS",
      description: "Responsabilité Civile & Décennale Solidaire",
      reference: "(Art. 1792 – Code civil)",
      icon: Shield,
      highlight: true
    },
    {
      title: "NOMINATIVE — RCDS au chantier",
      description: "Accompagnement civil & solidaire pour un chantier identifié",
      reference: "(Art. 1792 – Code civil)",
      icon: FileCheck,
      highlight: false
    },
    {
      title: "DOMIUM (DOS)",
      description: "Mécanisme coopératif d'entraide destiné aux constructeurs, artisans, maîtres d'œuvre et maîtres d'ouvrage confrontés à des difficultés techniques ou civiles post-travaux.",
      reference: null,
      icon: Handshake,
      highlight: false
    },
    {
      title: "CAP BÂTI",
      description: "Prévention et accompagnement des projets de construction de maisons individuelles.",
      reference: null,
      icon: Home,
      highlight: true
    },
    {
      title: "CAP BÂTI+",
      description: "Accompagnement solidaire des maîtres d’ouvrage et constructeurs confrontés à des difficultés de livraison ou de dépassement de prix.",
      reference: null,
      icon: Building2,
      highlight: false
    },
    {
      title: "ARCHOS — RCDS",
      description: "Soutien civil et solidaire pour les professions intellectuelles du bâtiment, l’ingénierie, la maîtrise d’œuvre et les missions techniques.",
      reference: null,
      icon: HardHat,
      highlight: false
    }
  ];

  const avantages = [
    { icon: Star, title: "Accompagnement", desc: "Étude de votre besoin et orientation" },
    { icon: Award, title: "Prévention", desc: "Analyse des situations techniques" },
    { icon: TrendingUp, title: "Traçabilité", desc: "Suivi du dossier et des échanges" },
    { icon: Globe, title: "Outre-mer", desc: "Modalités d’intervention selon le projet" }
  ];

  const etapes = [
    { title: 'Demande', desc: 'Décrire votre besoin et transmettre les pièces utiles.' },
    { title: 'Instruction', desc: 'Analyser les éléments techniques et la situation du projet.' },
    { title: 'Décision', desc: 'Examiner les suites possibles selon les règles du dispositif.' },
    { title: 'Suivi', desc: 'Organiser et suivre les actions retenues.' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Styles d'animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(212, 175, 55, 0.3); }
          50% { box-shadow: 0 0 40px rgba(212, 175, 55, 0.6); }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
        .animate-slide-up { animation: slide-up 0.6s ease-out forwards; }
        .animate-scale-in { animation: scale-in 0.5s ease-out forwards; }
        .card-hover-lift { transition: all 0.3s ease; }
        .card-hover-lift:hover { transform: translateY(-8px); box-shadow: 0 20px 40px rgba(0,0,0,0.15); }
      `}</style>

      {/* Header Premium avec image de fond */}
      <header className="relative bg-[#111] text-white overflow-hidden min-h-[70vh] flex items-center">
        {/* Image de fond - Professionnels BTP diversifiés */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_coop-dashboard-1/artifacts/gv52jglh_PRESENTATION%20COPPAM%20vierge%20%284%29.png')`
          }}
        />
        {/* Overlay avec dégradé premium */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#111]/95 via-[#111]/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111]/90 via-transparent to-[#111]/40" />
        
        {/* Motif décoratif */}
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
          <div className="absolute top-20 right-20 w-64 h-64 border-2 border-[#d4af37] rounded-full" />
          <div className="absolute top-40 right-40 w-48 h-48 border border-[#d4af37] rounded-full" />
        </div>
        
        <div className="relative z-10 py-20 px-6 w-full">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-left">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate(-1)}
                  className="mb-6 text-gray-400 hover:text-white hover:bg-white/10"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
                
                {/* Badge premium */}
                <div className="inline-flex items-center gap-2 bg-[#d4af37]/20 border border-[#d4af37]/50 text-[#d4af37] px-4 py-2 rounded-full text-sm font-medium mb-6 animate-pulse-glow">
                  <Shield className="w-4 h-4" />
                  Dispositif coopératif ESS
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                  COPPAM – <br/>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] to-[#f0d78c]">
                    Garanties Solidaires
                  </span>
                </h1>
                <p className="text-xl text-gray-300 mb-8 max-w-xl">
                  Un dispositif coopératif innovant de sécurisation des chantiers, basé sur l'entraide, 
                  la prévention, la solidarité et la gouvernance partagée.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <Button 
                    size="lg"
                    className="bg-[#d4af37] hover:bg-[#c4a030] text-white px-8 shadow-lg shadow-[#d4af37]/25"
                    onClick={() => navigate('/contact')}
                  >
                    Nous contacter
                  </Button>
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-white/50 text-white hover:bg-white/10 px-4 md:px-8"
                    onClick={() => navigate('/register')}
                  >
                    Devenir membre
                  </Button>
                </div>
              </div>
              
              {/* Étapes du parcours */}
              <div className="hidden lg:grid grid-cols-2 gap-4">
                {etapes.map((etape, index) => (
                  <div key={etape.title} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 animate-float" style={{animationDelay: `${index * 0.5}s`}}>
                    <p className="text-4xl font-bold text-[#d4af37]">0{index + 1}</p>
                    <p className="text-gray-300 text-sm">{etape.title}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Vague décorative en bas */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#f9fafb"/>
          </svg>
        </div>
      </header>

      {/* Bandeau d'avantages */}
      <section className="py-8 px-6 bg-white border-b">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {avantages.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="flex items-center gap-3 group">
                  <div className="p-2 bg-[#d4af37]/10 rounded-lg group-hover:bg-[#d4af37]/20 transition-colors">
                    <Icon className="w-5 h-5 text-[#d4af37]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#111] text-sm">{item.title}</p>
                    <p className="text-gray-500 text-xs">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Notre mission avec image premium */}
      <section id="mission" data-animate className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className={isVisible['mission'] ? 'animate-slide-up' : 'opacity-100'}>
              <span className="text-[#d4af37] font-semibold text-sm uppercase tracking-wider">Notre engagement</span>
              <h2 className="text-4xl font-bold text-[#111] mt-2 mb-6">Notre mission</h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 mb-4 text-lg leading-relaxed">
                  COPPAM – Garanties Solidaires accompagne les entreprises du bâtiment, les particuliers, 
                  les maîtres d'ouvrage et les porteurs de projets grâce à un mécanisme solidaire fondé sur 
                  la <strong className="text-[#d4af37]">mutualisation</strong> et le <strong className="text-[#d4af37]">droit civil</strong>.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  À travers un écosystème structuré (COPPAM / F.O.G.E.D.O.M / FOGEDOM-SCIC / O'SCOP), nous renforçons la{' '}
                  <strong>sécurisation des chantiers</strong>, la <strong>gestion des dommages</strong>,{' '}
                  l'<strong>aide post-travaux</strong> et la <strong>prévention</strong>.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {['Mutualisation', 'Droit civil', 'Solidarité', 'Prévention'].map((tag, i) => (
                  <span key={i} className="px-5 py-2 bg-gradient-to-r from-[#d4af37]/10 to-[#d4af37]/5 text-[#d4af37] rounded-full text-sm font-medium border border-[#d4af37]/20 hover:border-[#d4af37]/40 transition-colors cursor-default">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className={`relative ${isVisible['mission'] ? 'animate-scale-in' : 'opacity-100'}`}>
              {/* Image principale avec overlay */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80"
                  alt="Équipe de professionnels BTP en collaboration"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111]/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#d4af37] rounded-full flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-[#111]">Prévention et accompagnement</p>
                        <p className="text-sm text-gray-600">Au service des professionnels du BTP</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Badge flottant */}
              <div className="absolute -top-4 -right-4 bg-[#d4af37] text-white px-6 py-3 rounded-xl shadow-lg animate-float">
                <p className="text-xl font-bold">Entraide</p>
                <p className="text-xs">coopérative</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notre écosystème solidaire - Design Premium */}
      <section id="ecosysteme" data-animate className="py-20 px-6 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#d4af37] font-semibold text-sm uppercase tracking-wider">Organisation</span>
            <h2 className="text-4xl font-bold text-[#111] mt-2 mb-4">Notre écosystème solidaire</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Des rôles identifiés pour l’accueil, l’instruction, l’examen des demandes et le suivi
            </p>
          </div>
          
          {/* Image illustrative premium avec overlay */}
          <div className="mb-16 relative rounded-3xl overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1600&q=80"
              alt="Équipe de professionnels du BTP"
              className="w-full h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#111]/80 via-[#111]/40 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="p-12 max-w-xl">
                <h3 className="text-3xl font-bold text-white mb-4">Une équipe à votre service</h3>
                <p className="text-gray-200 text-lg">
                  Un accompagnement des projets du BTP, avec des modalités d’intervention précisées selon le territoire et le besoin
                </p>
              </div>
            </div>
          </div>
          
          {/* Cartes de l'écosystème avec design amélioré */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ecosysteme.map((item, index) => (
              <Card key={index} className={`card-hover-lift bg-white overflow-hidden border-0 shadow-lg ${isVisible['ecosysteme'] ? 'animate-slide-up' : 'opacity-100'}`} style={{animationDelay: `${index * 0.1}s`}}>
                <div className={`h-1.5 bg-gradient-to-r ${item.color}`} />
                <CardContent className="pt-8 pb-6">
                  <div className="text-5xl mb-5 transform hover:scale-110 transition-transform duration-300">{item.icon}</div>
                  <h3 className="text-lg font-bold text-[#111] mb-3 leading-tight">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Ligne de connexion visuelle */}
          <div className="hidden lg:flex justify-center mt-8">
            <div className="flex items-center gap-4">
              <div className="w-3 h-3 bg-amber-500 rounded-full" />
              <div className="w-24 h-0.5 bg-gradient-to-r from-amber-500 to-blue-500" />
              <div className="w-3 h-3 bg-blue-500 rounded-full" />
              <div className="w-24 h-0.5 bg-gradient-to-r from-blue-500 to-emerald-500" />
              <div className="w-3 h-3 bg-emerald-500 rounded-full" />
              <div className="w-24 h-0.5 bg-gradient-to-r from-emerald-500 to-purple-500" />
              <div className="w-3 h-3 bg-purple-500 rounded-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Nos garanties solidaires - Design Premium */}
      <section id="garanties" data-animate className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#d4af37] font-semibold text-sm uppercase tracking-wider">Nos solutions</span>
            <h2 className="text-4xl font-bold text-[#111] mt-2 mb-4">Nos garanties solidaires</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Des dispositifs adaptés à chaque besoin, fondés sur le Code civil et la solidarité coopérative
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {garanties.map((garantie, index) => {
              const Icon = garantie.icon;
              return (
                <Card key={index} className={`card-hover-lift bg-white overflow-hidden border-0 shadow-lg ${garantie.highlight ? 'ring-2 ring-[#d4af37]/50' : ''} ${isVisible['garanties'] ? 'animate-slide-up' : 'opacity-100'}`} style={{animationDelay: `${index * 0.1}s`}}>
                  {garantie.highlight && (
                    <div className="bg-gradient-to-r from-[#d4af37] to-[#f0d78c] text-white text-center py-1 text-xs font-semibold">
                      DÉCOUVRIR
                    </div>
                  )}
                  <div className={`h-1.5 bg-gradient-to-r from-[#d4af37] to-[#e8c55a] ${garantie.highlight ? 'hidden' : ''}`} />
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-4 rounded-xl ${garantie.highlight ? 'bg-[#d4af37] text-white' : 'bg-[#d4af37]/10'} transition-all duration-300`}>
                        <Icon className={`w-7 h-7 ${garantie.highlight ? 'text-white' : 'text-[#d4af37]'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-[#111] mb-3">{garantie.title}</h3>
                        <p className="text-gray-700 text-sm mb-3 leading-relaxed">{garantie.description}</p>
                        {garantie.reference && (
                          <p className="text-xs text-gray-500 italic bg-gray-50 px-3 py-2 rounded-lg">{garantie.reference}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Parcours de demande */}
      <section className="py-20 px-6 bg-[#111] relative overflow-hidden">
        {/* Motif de fond */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#d4af37] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#d4af37] rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Comment présenter votre demande ?</h2>
            <p className="text-gray-400">Les conditions d’accès et d’intervention sont précisées avant tout engagement.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {etapes.map((etape, index) => (
              <div key={etape.title} className="group">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 group-hover:bg-white/10 transition-colors h-full">
                  <p className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] to-[#f0d78c]">0{index + 1}</p>
                  <h3 className="text-white font-semibold mt-3">{etape.title}</h3>
                  <p className="text-gray-400 text-sm mt-3">{etape.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Premium */}
      <section className="py-24 px-6 relative overflow-hidden">
        {/* Image de fond */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/95 via-[#1e3a5f]/90 to-[#2d5a87]/85" />
        
        {/* Motif décoratif */}
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <div className="absolute top-20 right-20 w-80 h-80 border-2 border-white rounded-full" />
          <div className="absolute top-40 right-40 w-60 h-60 border border-white rounded-full" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Images de partenariat */}
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_coop-dashboard-1/artifacts/bbxt0sni_PRESENTATION%20COPPAM%20vierge%20%283%29.png"
                  alt="Partenariat professionnel"
                  className="w-full h-72 object-cover rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-[#d4af37] p-6 rounded-xl shadow-xl">
                  <Users className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
            
            <div className="text-white">
              <span className="text-[#d4af37] font-semibold text-sm uppercase tracking-wider">Rejoignez-nous</span>
              <h2 className="text-4xl font-bold mt-2 mb-6">Besoin d'accompagnement ?</h2>
              <p className="text-gray-200 mb-8 text-lg leading-relaxed">
                Notre équipe est à votre disposition pour étudier votre situation et vous orienter 
                vers la solution adaptée à vos besoins. Rejoignez notre réseau de professionnels solidaires 
                et bénéficiez d'un accompagnement personnalisé.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  className="bg-[#d4af37] hover:bg-[#c4a030] text-white px-8 shadow-lg shadow-[#d4af37]/25"
                  onClick={() => navigate('/contact')}
                >
                  Nous contacter
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 px-4 md:px-8"
                  onClick={() => navigate('/register')}
                >
                  Devenir membre
                </Button>
              </div>
              
              {/* Points clés */}
              <div className="mt-10 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#d4af37]" />
                  <span className="text-gray-300 text-sm">Accompagnement personnalisé</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#d4af37]" />
                  <span className="text-gray-300 text-sm">Équipe dédiée</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#d4af37]" />
                  <span className="text-gray-300 text-sm">Dispositifs adaptés</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#d4af37]" />
                  <span className="text-gray-300 text-sm">Réseau solidaire</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Premium */}
      <footer className="bg-[#111] text-white py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-[#d4af37]/20 rounded-lg">
                <Shield className="w-8 h-8 text-[#d4af37]" />
              </div>
              <span className="text-xl font-bold">COPPAM – Garanties Solidaires</span>
            </div>
            <p className="text-gray-500 text-sm mb-8 text-center">
              Dispositif coopératif ESS • Économie Sociale et Solidaire
            </p>
            
            {/* Logo CRESS Île-de-France */}
            <div className="bg-white rounded-xl p-4 shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_coop-dashboard-1/artifacts/d86falg7_logo-CRESS-IDF-299x120-1.png"
                alt="CRESS - Chambre Régionale de l'Économie Sociale et Solidaire Île-de-France"
                className="h-16 object-contain"
              />
            </div>
            
            <div className="mt-8 pt-8 border-t border-white/10 w-full text-center">
              <p className="text-gray-600 text-xs">© 2026 COPPAM – Garanties Solidaires. Tous droits réservés.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
